import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  catalogWidgetQuery,
  rotateCatalogWidgetKey,
  saveCatalogWidget,
  widgetSnippet,
} from '../../api/settingsCatalog'
import { toast } from '../../ui/notify'

/**
 * "Embed in your intranet" - widgetul magazinului (cerinta CEC 33).
 *
 * Omul scrie adresele intranetului (una pe rand), aprinde widgetul si copiaza
 * cele doua randuri in pagina. Cheia e PUBLICA - spune doar a cui firma e
 * catalogul; ce apara cu adevarat e lista de adrese (verificata de server la
 * fiecare cerere, prin antetul `Origin`) si intrarea in portal a fiecarui om.
 */
function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

export function CatalogWidgetPanel({ portalOrigin = window.location.origin }: { portalOrigin?: string }) {
  const client = useQueryClient()
  const widget = useQuery(catalogWidgetQuery())
  const [enabled, setEnabled] = useState(false)
  const [origins, setOrigins] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!widget.data) return
    setEnabled(widget.data.enabled)
    setOrigins(widget.data.allowed_origins.join('\n'))
  }, [widget.data])

  if (widget.isError) {
    return <p className="text-muted">The intranet widget settings could not be read.</p>
  }
  if (!widget.data) return null

  const data = widget.data
  const editable = data.canWrite && data.available
  const snippet = data.public_key !== '' ? widgetSnippet(portalOrigin, data.public_key) : ''

  const after = (promise: Promise<{ status: string; message: string }>) => {
    setBusy(true)
    void promise
      .then((result) => {
        toast(result.message, 'Intranet widget', result.status === 'OK' ? 'success' : 'error')
        void client.invalidateQueries({ queryKey: ['settings', 'catalog', 'widget'] })
      })
      .catch((error: unknown) => toast(failed(error), 'Intranet widget', 'error'))
      .finally(() => setBusy(false))
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    after(saveCatalogWidget({ enabled, allowed_origins: origins }))
  }

  return (
    <section id="catalogWidget" style={{ marginBottom: 24 }}>
      <header className="panel-heading">
        <h3>
          <strong>Embed in your intranet</strong>
        </h3>
        <label className="color">
          the service shop of {data.company_name || 'this company'} on any page of your intranet
        </label>
      </header>

      {!data.available && (
        <p className="text-muted">
          <i className="fas fa-database"></i> Not available yet - the widget migration has not run on this server.
        </p>
      )}

      <form id="catalogWidgetForm" className="form-horizontal" onSubmit={submit}>
        <div className="form-group">
          <div className="col-sm-12">
            <label className="adm-check">
              <input
                type="checkbox"
                name="enabled"
                checked={enabled}
                disabled={!editable}
                onChange={(event) => setEnabled(event.target.checked)}
              />
              <span>
                <strong>Widget on</strong> &mdash; the snippet below answers only while this is ticked
              </span>
            </label>

            <label htmlFor="catalogWidgetOrigins" style={{ display: 'block', marginTop: 10 }}>
              Intranet addresses allowed to show it
            </label>
            <textarea
              id="catalogWidgetOrigins"
              name="allowed_origins"
              className="form-control"
              rows={3}
              placeholder={'https://intranet.example.com\nhttps://*.example.com'}
              value={origins}
              disabled={!editable}
              onChange={(event) => setOrigins(event.target.value)}
            />
            <p className="text-muted" style={{ margin: '4px 0 10px' }}>
              One per line: <code>https://host</code>, or <code>https://*.domain</code> for every sub-domain. Only
              https (plain http only for localhost). Any other page gets nothing back.
            </p>

            {editable && (
              <button type="submit" className="btn btn-theme btn-sm" disabled={busy}>
                Save
              </button>
            )}
          </div>
        </div>
      </form>

      {snippet !== '' && (
        <div className="form-group">
          <label htmlFor="catalogWidgetSnippet">Paste this where the catalogue should appear</label>
          <textarea
            id="catalogWidgetSnippet"
            className="form-control"
            rows={3}
            readOnly
            value={snippet}
            onFocus={(event) => event.target.select()}
            style={{ fontFamily: 'monospace', fontSize: 12 }}
          />
          <p className="text-muted" style={{ margin: '4px 0 10px' }}>
            People sign in through a portal window (password and second factor, or your company&rsquo;s single sign-on).
            No cookie is set on the intranet. If the intranet has a Content-Security-Policy, allow{' '}
            <code>{portalOrigin}</code> in <code>script-src</code> and <code>connect-src</code>, and the file server in{' '}
            <code>img-src</code>.
          </p>
          {editable && (
            <button
              type="button"
              id="catalogWidgetRotate"
              className="btn btn-default btn-sm"
              disabled={busy}
              onClick={() => after(rotateCatalogWidgetKey())}
            >
              <i className="fas fa-sync-alt"></i> New key (the old snippet stops working)
            </button>
          )}
        </div>
      )}
    </section>
  )
}
